import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const reportingDefinitionsConfigurationValueSchema = z.strictObject({
    maximumDefinitions: z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)]),
    maximumRowsPerRun: z.union([z.literal(100), z.literal(250), z.literal(500)]),
    maximumFiltersPerDefinition: z.number().int().min(1).max(10),
    defaultRowLimit: z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)]),
}).superRefine((value, context) => {
    if (value.defaultRowLimit > value.maximumRowsPerRun) {
        context.addIssue({
            code: 'custom', path: ['defaultRowLimit'],
            message: 'The default row limit cannot exceed the dataset execution limit.',
        });
    }
});
export const reportingDefinitionsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'reporting.definitions.configuration', moduleId: 'reporting',
        title: 'Reporting behavior',
        helpText: 'Controls bounded tenant report-definition and execution limits.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 2,
        codedFallback: {
            maximumDefinitions: 100, maximumRowsPerRun: 500,
            maximumFiltersPerDefinition: 10, defaultRowLimit: 100,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: reportingDefinitionsConfigurationValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                const { maximumProjectsPerRun, ...retained } = source;
                return { ...retained, maximumRowsPerRun: maximumProjectsPerRun ?? 500 };
            },
        }],
});
export const reportingDefinitionsDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['small', 'medium', 'large']),
    showDefinitionDescriptions: z.boolean(),
    showTotals: z.boolean(),
    showVersionMetadata: z.boolean(),
    tableLayout: z.enum(['fit', 'scroll']),
});
export const reportingDefinitionsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'reporting.definitions.display', moduleId: 'reporting',
        title: 'Reporting display',
        helpText: 'Controls report cards, density, totals, version metadata, and table layout.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            density: 'comfortable', cardSize: 'medium', showDefinitionDescriptions: true,
            showTotals: true, showVersionMetadata: true, tableLayout: 'scroll',
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: reportingDefinitionsDisplayValueSchema,
});
const labelSchema = z.string().trim().min(1).max(120);
const statusLabelsSchema = z.strictObject({
    draft: labelSchema, active: labelSchema, archived: labelSchema,
});
const unavailableReasonLabelsSchema = z.strictObject({
    'not-installed': labelSchema,
    'not-enabled': labelSchema,
    'permission-denied': labelSchema,
    'provider-invalid': labelSchema,
    'provider-failed': labelSchema,
    'definition-not-active': labelSchema,
    'configuration-invalid': labelSchema,
});
const columnLabelsSchema = z.strictObject({
    projectNumber: labelSchema, projectName: labelSchema, projectStatus: labelSchema,
    invoiceCount: labelSchema, invoicedCents: labelSchema, collectedCents: labelSchema,
    writtenOffCents: labelSchema, outstandingCents: labelSchema,
    approvedDirectExpenseCount: labelSchema, approvedDirectExpenseCents: labelSchema,
    referralCommissionCount: labelSchema, referralCommissionCents: labelSchema,
    overheadCents: labelSchema, knownFinanceCostCents: labelSchema,
    knownFinanceContributionCents: labelSchema,
    membershipDisplayName: labelSchema, assignmentTotal: labelSchema, assignmentOpen: labelSchema,
    assignmentCompleted: labelSchema, assignmentWaived: labelSchema, assignmentOverdue: labelSchema,
    completedCourseCount: labelSchema, assessmentAttemptCount: labelSchema,
    assessmentPassedCount: labelSchema, averageScoreBasisPoints: labelSchema,
    latestAssessmentSubmittedAt: labelSchema, activitySessionCount: labelSchema,
    activityActiveSeconds: labelSchema, latestActivityAt: labelSchema,
});
export const reportingDefinitionsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().min(1).max(60), title: z.string().min(1).max(100),
    description: z.string().min(1).max(300), builtInTitle: z.string().min(1).max(120),
    builtInDescription: z.string().min(1).max(300), createAction: z.string().min(1).max(80),
    saveVersionAction: z.string().min(1).max(80), publishAction: z.string().min(1).max(80),
    archiveAction: z.string().min(1).max(80), rollbackAction: z.string().min(1).max(80),
    runAction: z.string().min(1).max(80), nameLabel: z.string().min(1).max(80),
    descriptionLabel: z.string().min(1).max(80), columnsLabel: z.string().min(1).max(80),
    filtersLabel: z.string().min(1).max(80), sortLabel: z.string().min(1).max(80),
    sortDirectionLabel: z.string().min(1).max(80), ascendingLabel: z.string().min(1).max(80),
    descendingLabel: z.string().min(1).max(80),
    rowLimitLabel: z.string().min(1).max(80), chartLabel: z.string().min(1).max(80),
    tableLabel: z.string().min(1).max(80), barLabel: z.string().min(1).max(80),
    reasonLabel: z.string().min(1).max(80),
    initialReason: z.string().min(5).max(500), nextVersionReason: z.string().min(5).max(500),
    versionShortLabel: z.string().min(1).max(20), revisionShortLabel: z.string().min(1).max(20),
    resultCountTemplate: z.string().min(1).max(160).superRefine((value, context) => {
        for (const placeholder of ['{returned}', '{total}']) {
            if (!value.includes(placeholder)) {
                context.addIssue({ code: 'custom', message: `Result count copy requires ${placeholder}.` });
            }
        }
    }),
    loadingLabel: z.string().min(1).max(120), savingLabel: z.string().min(1).max(120),
    loadError: z.string().min(1).max(240), mutationError: z.string().min(1).max(240),
    unavailableTitle: z.string().min(1).max(160), unavailableDescription: z.string().min(1).max(300),
    truncatedLabel: z.string().min(1).max(180), emptyTitle: z.string().min(1).max(120),
    emptyDescription: z.string().min(1).max(300), financeContributionLabel: z.string().min(1).max(120),
    datasetLabel: labelSchema,
    datasetLabels: z.strictObject({
        'finance-project-performance': labelSchema,
        'training-team-summary': labelSchema,
    }),
    trainingBuiltInTitle: z.string().min(1).max(120),
    trainingBuiltInDescription: z.string().min(1).max(300),
    trainingResultCountTemplate: z.string().min(1).max(160).superRefine((value, context) => {
        for (const placeholder of ['{returned}', '{total}']) {
            if (!value.includes(placeholder)) {
                context.addIssue({ code: 'custom', message: `Training result copy requires ${placeholder}.` });
            }
        }
    }),
    trainingAttentionNotice: z.string().min(20).max(300),
    trainingCompletionLabel: z.string().min(1).max(120),
    totalsLabel: z.string().min(1).max(80),
    statusLabels: statusLabelsSchema, unavailableReasonLabels: unavailableReasonLabelsSchema,
    columnLabels: columnLabelsSchema,
});
const reportingTrainingColumnLabels = {
    membershipDisplayName: 'Team member', assignmentTotal: 'Assignments',
    assignmentOpen: 'Open assignments', assignmentCompleted: 'Completed assignments',
    assignmentWaived: 'Waived assignments', assignmentOverdue: 'Overdue assignments',
    completedCourseCount: 'Completed courses', assessmentAttemptCount: 'Submitted attempts',
    assessmentPassedCount: 'Passed attempts', averageScoreBasisPoints: 'Average score',
    latestAssessmentSubmittedAt: 'Latest assessment', activitySessionCount: 'Activity sessions',
    activityActiveSeconds: 'Training attention', latestActivityAt: 'Latest Training activity',
};
const reportingDefinitionsCopyVersionTwoAdditions = {
    datasetLabel: 'Dataset',
    datasetLabels: {
        'finance-project-performance': 'Finance project performance',
        'training-team-summary': 'Training team summary',
    },
    trainingBuiltInTitle: 'Training team summary',
    trainingBuiltInDescription: 'Active team membership with aggregate Training assignments, completions, scores, and non-payable attention evidence.',
    trainingResultCountTemplate: 'Showing {returned} of {total} active team members',
    trainingAttentionNotice: 'Training attention is supporting learning evidence only. It is not a timecard, payroll, or payable-time fact.',
    trainingCompletionLabel: 'Completed courses',
};
export const reportingDefinitionsCopySetting = definePlatformSetting({
    definition: {
        key: 'reporting.copy.definitions', moduleId: 'reporting', title: 'Reporting copy',
        helpText: 'Controls tenant-editable Reporting labels and explanatory copy.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Reporting', title: 'Reporting',
            description: 'Build versioned reports from permission-respecting owner projections.',
            builtInTitle: 'Finance project performance',
            builtInDescription: 'Project identity with exact Finance-owned revenue, expense, commission, overhead, and known contribution.',
            createAction: 'Create report', saveVersionAction: 'Save new version',
            publishAction: 'Publish version', archiveAction: 'Archive report',
            rollbackAction: 'Roll back', runAction: 'Run report', nameLabel: 'Report name',
            descriptionLabel: 'Description', columnsLabel: 'Columns', filtersLabel: 'Filters (JSON)',
            sortLabel: 'Sort field', sortDirectionLabel: 'Sort direction',
            ascendingLabel: 'Ascending', descendingLabel: 'Descending',
            rowLimitLabel: 'Maximum rows', chartLabel: 'Presentation',
            tableLabel: 'Table', barLabel: 'Bar chart', reasonLabel: 'Change reason',
            initialReason: 'Create the initial Reporting definition',
            nextVersionReason: 'Describe the next Reporting definition version',
            versionShortLabel: 'v', revisionShortLabel: 'r',
            resultCountTemplate: 'Showing {returned} of {total} Projects',
            loadingLabel: 'Loading reports…', savingLabel: 'Saving report…',
            loadError: 'Reporting could not be loaded.',
            mutationError: 'The report operation failed. Refresh and try again.',
            unavailableTitle: 'Report data is unavailable',
            unavailableDescription: 'A required owner projection is absent, disabled, or not permitted. No missing values were treated as zero.',
            truncatedLabel: 'This result is truncated by the configured project or row limit.',
            emptyTitle: 'No custom reports yet',
            emptyDescription: 'Use the built-in report now, or create a versioned tenant report.',
            financeContributionLabel: 'Known Finance contribution', totalsLabel: 'Totals',
            ...reportingDefinitionsCopyVersionTwoAdditions,
            statusLabels: { draft: 'Draft', active: 'Active', archived: 'Archived' },
            unavailableReasonLabels: {
                'not-installed': 'Required owner module is not installed',
                'not-enabled': 'Required owner module is not enabled',
                'permission-denied': 'You do not have permission to read the required facts',
                'provider-invalid': 'Required owner facts failed contract validation',
                'provider-failed': 'Required owner facts could not be loaded',
                'definition-not-active': 'The selected report is not published and active',
                'configuration-invalid': 'The report exceeds the tenant execution limits',
            },
            columnLabels: {
                projectNumber: 'Project number', projectName: 'Project', projectStatus: 'Status',
                invoiceCount: 'Invoices', invoicedCents: 'Invoiced', collectedCents: 'Collected',
                writtenOffCents: 'Written off', outstandingCents: 'Outstanding',
                approvedDirectExpenseCount: 'Approved expenses',
                approvedDirectExpenseCents: 'Direct expense amount',
                referralCommissionCount: 'Referral commissions',
                referralCommissionCents: 'Referral commission amount', overheadCents: 'Overhead',
                knownFinanceCostCents: 'Known Finance cost',
                knownFinanceContributionCents: 'Known Finance contribution',
                ...reportingTrainingColumnLabels,
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: reportingDefinitionsCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                const columnLabels = typeof source.columnLabels === 'object' && source.columnLabels !== null &&
                    !Array.isArray(source.columnLabels)
                    ? source.columnLabels
                    : {};
                return {
                    ...source,
                    ...reportingDefinitionsCopyVersionTwoAdditions,
                    columnLabels: {
                        ...columnLabels,
                        ...reportingTrainingColumnLabels,
                    },
                };
            },
        }],
});
const ianaTimeZone = z.string().trim().min(1).max(100).refine((value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
    }
    catch {
        return false;
    }
}, 'Expected an IANA time zone.');
export const reportingSchedulesConfigurationValueSchema = z.strictObject({
    maximumSchedules: z.union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)]),
    maximumRecipients: z.union([z.literal(10), z.literal(25), z.literal(50)]),
    maximumCsvBytes: z.union([
        z.literal(65_536), z.literal(262_144), z.literal(1_048_576), z.literal(5_242_880),
    ]),
    artifactRetentionDays: z.number().int().min(1).max(30),
    defaultTimeZone: ianaTimeZone,
    workerBatchSize: z.number().int().min(1).max(100),
    workerLeaseSeconds: z.number().int().min(30).max(900),
});
export const reportingSchedulesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'reporting.schedules.configuration', moduleId: 'reporting',
        title: 'Reporting schedules',
        helpText: 'Controls tenant schedule, recipient, CSV, retention, and worker bounds.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            maximumSchedules: 25, maximumRecipients: 25, maximumCsvBytes: 1_048_576,
            artifactRetentionDays: 7, defaultTimeZone: 'America/Indiana/Indianapolis',
            workerBatchSize: 25, workerLeaseSeconds: 300,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: reportingSchedulesConfigurationValueSchema,
});
export const reportingSchedulesCopyValueSchema = z.strictObject({
    sectionTitle: labelSchema, sectionDescription: z.string().trim().min(1).max(300),
    createAction: labelSchema, updateAction: labelSchema, pauseAction: labelSchema,
    resumeAction: labelSchema, archiveAction: labelSchema, rollbackAction: labelSchema,
    downloadCsvAction: labelSchema, scheduleNameLabel: labelSchema, sourceLabel: labelSchema,
    cadenceLabel: labelSchema, dailyLabel: labelSchema, weeklyLabel: labelSchema,
    monthlyLabel: labelSchema, weekDayLabel: labelSchema, monthDayLabel: labelSchema,
    timeLabel: labelSchema, timeZoneLabel: labelSchema, recipientsLabel: labelSchema,
    subjectLabel: labelSchema, bodyLabel: labelSchema, nextRunLabel: labelSchema,
    lastOutcomeLabel: labelSchema, activeLabel: labelSchema, pausedLabel: labelSchema,
    archivedLabel: labelSchema,
    builtInFinanceSourceLabel: labelSchema,
    builtInTrainingSourceLabel: labelSchema,
    noSchedulesTitle: labelSchema, noSchedulesDescription: z.string().trim().min(1).max(300),
    scheduleReason: z.string().trim().min(5).max(500),
    updateReason: z.string().trim().min(5).max(500),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    deliverySubject: z.string().trim().min(1).max(240),
    deliveryBody: z.string().trim().min(1).max(10_000),
    unavailableCsvLabel: z.string().trim().min(1).max(240),
    weekDayLabels: z.strictObject({
        monday: labelSchema, tuesday: labelSchema, wednesday: labelSchema,
        thursday: labelSchema, friday: labelSchema, saturday: labelSchema, sunday: labelSchema,
    }),
    outcomeLabels: z.strictObject({
        'not-installed': labelSchema, 'not-enabled': labelSchema,
        'permission-denied': labelSchema, 'provider-invalid': labelSchema,
        'provider-failed': labelSchema, 'definition-not-active': labelSchema,
        'configuration-invalid': labelSchema, 'actor-or-module-unavailable': labelSchema,
        'actor-unavailable': labelSchema, 'communications-not-installed': labelSchema,
        'communications-not-enabled': labelSchema, 'delivery-not-permitted': labelSchema,
        'csv-too-large': labelSchema, 'all-recipients-suppressed': labelSchema,
        'delivery-queued': labelSchema, 'delivery-queued-partial': labelSchema,
        'transient-preparation-failure': labelSchema,
    }),
    unknownOutcomeLabel: labelSchema,
});
export const reportingSchedulesCopySetting = definePlatformSetting({
    definition: {
        key: 'reporting.copy.schedules', moduleId: 'reporting', title: 'Reporting schedule copy',
        helpText: 'Controls tenant-editable schedule, CSV, and internal-delivery wording.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 2,
        codedFallback: {
            sectionTitle: 'Scheduled reports',
            sectionDescription: 'Prepare tenant-scoped CSV reports for selected active team members.',
            createAction: 'Create schedule', updateAction: 'Save new schedule version',
            pauseAction: 'Pause', resumeAction: 'Resume', archiveAction: 'Archive',
            rollbackAction: 'Roll back', downloadCsvAction: 'Download CSV',
            scheduleNameLabel: 'Schedule name', sourceLabel: 'Report definition',
            cadenceLabel: 'Cadence', dailyLabel: 'Daily', weeklyLabel: 'Weekly',
            monthlyLabel: 'Monthly', weekDayLabel: 'Weekday', monthDayLabel: 'Day of month',
            timeLabel: 'Local time', timeZoneLabel: 'Time zone', recipientsLabel: 'Recipients',
            subjectLabel: 'Email subject', bodyLabel: 'Email message', nextRunLabel: 'Next run',
            lastOutcomeLabel: 'Last outcome', activeLabel: 'Active', pausedLabel: 'Paused',
            archivedLabel: 'Archived',
            builtInFinanceSourceLabel: 'Finance project performance',
            builtInTrainingSourceLabel: 'Training team summary',
            noSchedulesTitle: 'No report schedules yet',
            noSchedulesDescription: 'Create a daily, weekly, or monthly CSV schedule for active team members.',
            scheduleReason: 'Create the initial Reporting schedule',
            updateReason: 'Save the next Reporting schedule version',
            loadError: 'Scheduled reports could not be loaded.',
            mutationError: 'The scheduled report operation failed. Refresh and try again.',
            deliverySubject: 'CREST scheduled report',
            deliveryBody: 'Your scheduled CREST report is attached as a CSV file.',
            unavailableCsvLabel: 'CSV export is unavailable because the report could not be completed.',
            weekDayLabels: {
                monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
                friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
            },
            outcomeLabels: {
                'not-installed': 'Required module is not installed',
                'not-enabled': 'Required module is not enabled',
                'permission-denied': 'Required access is unavailable',
                'provider-invalid': 'A report data provider returned invalid data',
                'provider-failed': 'A report data provider failed',
                'definition-not-active': 'The report definition is not active',
                'configuration-invalid': 'The report exceeds the tenant limits',
                'actor-or-module-unavailable': 'The schedule actor or Reporting module is unavailable',
                'actor-unavailable': 'The schedule actor is unavailable',
                'communications-not-installed': 'Communications is not installed',
                'communications-not-enabled': 'Communications is not enabled',
                'delivery-not-permitted': 'Internal report delivery is not permitted',
                'csv-too-large': 'The CSV exceeded the configured size limit',
                'all-recipients-suppressed': 'Every recipient was suppressed',
                'delivery-queued': 'Delivery was queued',
                'delivery-queued-partial': 'Delivery was queued for some recipients',
                'transient-preparation-failure': 'Report preparation will be retried',
            },
            unknownOutcomeLabel: 'Unknown outcome',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: reportingSchedulesCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => {
                const source = typeof value === 'object' && value !== null && !Array.isArray(value)
                    ? value
                    : {};
                const { builtInSourceLabel, ...retained } = source;
                return {
                    ...retained,
                    builtInFinanceSourceLabel: builtInSourceLabel ?? 'Finance project performance',
                    builtInTrainingSourceLabel: 'Training team summary',
                };
            },
        }],
});
export const reportingSettingRegistrations = [
    reportingDefinitionsConfigurationSetting,
    reportingDefinitionsDisplaySetting,
    reportingDefinitionsCopySetting,
    reportingSchedulesConfigurationSetting,
    reportingSchedulesCopySetting,
];
//# sourceMappingURL=reporting-setting-definitions.js.map